import * as React from 'react';
import { History, UnregisterCallback } from 'history';
import slugify from 'slugify';
import Button from 'src/components/Button';
import Input from 'src/components/FormElements/Input';
import { IPage, IPageAttributes, PageStatus } from 'src/Entities/Pages';
import FieldError from 'src/components/FormElements/FieldError';
import { addToast, clearToasts } from 'src/components/Toast';
import { addImageToPage } from 'src/helpers/api';
import ContentEditor from 'src/components/ContentEditor';
import { Editor } from 'tinymce';

interface IPageFormProps {
  page: IPage;
  history: History;
  onSubmit: (values: IPage) => Promise<any>;
  onCancel: () => void;
  onDelete: (page: IPage) => void;
  onChangeStatus: (status: PageStatus, page: IPage) => void;
}

export interface IPageFormValues {
  title: string;
  content: string;
}

interface IPageFormErrors {
  [key: string]: string;
}

const validate = (values: IPageFormValues): null | IPageFormErrors => {
  const errors: IPageFormErrors = {};
  let valid = true;

  if (values.title.trim() === '') {
    valid = false;
    errors.title = 'skal udfyldes';
  }

  return valid ? null : errors;
};

const PageForm = ({
  page,
  history,
  onCancel,
  onDelete,
  onSubmit,
  onChangeStatus
}: IPageFormProps) => {
  const [values, setValues] = React.useState<IPageFormValues>(
    () => ({ title: page.title, content: page.content })
  );
  const editorRef = React.useRef<Editor | null>(null);
  const [errors, setErrors] = React.useState<IPageFormErrors>({});
  const unblock = React.useRef<UnregisterCallback | null>(null);
  const previousPageRef = React.useRef(page);
  const formRef = React.useRef({
    page,
    values,
  });

  React.useEffect(() => {
    formRef.current = {
      page,
      values,
    };
  }, [page, values]);

  React.useEffect(() => {
    const { current: previousPage } = previousPageRef;

    if (previousPage.portalPageId === page.portalPageId) {
      return;
    }

    previousPageRef.current = page;

    unblock.current = history.block(() => {
      const { current } = formRef;

      const didTitleChange = current.page.title !== current.values.title;
      const didContentChange = current.page.content !== (editorRef.current?.getContent() || '');

      if (didTitleChange || didContentChange) {
        if (
          !window.confirm(
            'Du har ændringer, som ikke er gemt. Er du sikker på, at du vil fortsætte?'
          )
        ) {
          return false;
        }
      }

      return;
    });

    const editor = editorRef.current?.setContent(page.content);
    setValues({
      title: page.title,
      content: page.content || ''
    });
    setErrors({});

    return () => {
      if (unblock.current) {
        unblock.current();
      }

      unblock.current = null;
    };
  }, [page]);

  const handleTitleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { target } = event;
      setValues({ ...values, title: target.value });
    },
    [values]
  );
  const submit = React.useCallback((pageAttributes?: IPageAttributes) => {
    const validationErrors = validate(values);
    if (validationErrors !== null) {
      setErrors(validationErrors);
      addToast({
        type: 'error',
        message: 'Formularen er ikke udfyldt korrekt'
      });
      return;
    }

    setErrors({});

    const attributes = {
      ...page,
      ...values,
      slug: `${page.portalPageId}-${slugify(values.title)}`,
      status: page.status === PageStatus.DRAFTED ? PageStatus.UNPUBLISHED : page.status,
      content: editorRef.current?.getContent() || ''
    };

    if (pageAttributes) {
      Object.assign(attributes, pageAttributes);
    }

    onSubmit(attributes);
  }, [values, page]);

  const handleSubmitAndPublish = () => {
    submit({ status: PageStatus.PUBLISHED });
  };

  const handleToggleStatusClick = React.useCallback(() => {
    const status =
      page.status === PageStatus.PUBLISHED
        ? PageStatus.UNPUBLISHED
        : PageStatus.PUBLISHED;

    onChangeStatus(status, page);
  }, [page]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    submit();
  };

  const handleCancelClick = () => {
    editorRef.current?.setContent(page.content || '');
    setValues({ title: page.title, content: page.content || '' });
    setErrors({});

    setTimeout(onCancel, 0);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-6">
        <label htmlFor="title" className="font-bold f5 uppercase block mb-1">
          Overskrift
        </label>
        <Input
          type="text"
          name="title"
          id="title"
          variant="inset"
          elementSize="large"
          className="w-full block"
          autoComplete="off"
          autoFocus={true}
          value={values.title}
          onChange={handleTitleChange}
          showErrorBorder={errors['title'] !== undefined}
        />
        {errors['title'] && (
          <FieldError className="mt-1">{errors['title']}</FieldError>
        )}
      </div>
      {page.status !== PageStatus.DRAFTED && (
        <div className="mb-6">
          <label htmlFor="content" className="font-bold f5 uppercase block mb-1">
            Synlighed
          </label>
          <Button
            type="button"
            className="mr-auto"
            color={page.status === PageStatus.PUBLISHED ? 'danger' : 'primary'}
            onClick={handleToggleStatusClick}
          >
            {page.status === PageStatus.PUBLISHED
              ? 'Skjul siden for brugere'
              : 'Vis siden for brugere'}
          </Button>
        </div>
      )}
      <div className="mb-6">
        <label htmlFor="content" className="font-bold f5 uppercase block mb-1">
          Indhold
        </label>
        <ContentEditor
          ref={editorRef}
          initialValue={page.content}
          onUpload={(file) => {
            return addImageToPage(page.portalPageId, file)
              .then((images) => images[0]);
          }}
        />
      </div>

      <div className="flex justify-end mt-6">
        {page.parentPageId && (
          <Button
            type="button"
            color="danger"
            className="mr-auto"
            onClick={() => onDelete(page)}
          >
            Slet side
          </Button>
        )}
        <Button type="button" color="gray" onClick={handleCancelClick}>
          Fortryd
        </Button>
        <Button type="submit" className="ml-2">
          {page.status === PageStatus.DRAFTED ? 'Gem kladde' : 'Opdater'}
        </Button>
        {page.status === PageStatus.DRAFTED && (
          <Button type="button" onClick={handleSubmitAndPublish} className="ml-2">
            Gem og udgiv
          </Button>
        )}
      </div>
    </form>
  );
};

export default PageForm;



// WEBPACK FOOTER //
// ./src/Pages/PageEditor/components/PageForm.tsx